import React, { useEffect } from "react"
import { useIntl } from "react-intl"
import { Button, CurrencyIcon, Ellipser, useCopyClick, useShare } from "@btc-alpha/ui-components"

import commonMessages from "messages/common"
import financeMessages from "messages/finance"
import historyMessages from "messages/history"
import { TDefaultModalProps } from "hooks/useModal"
import { splitWords } from "utils/format"
import { formatDate } from "utils/formatDate"
import { capitalizeFirstLetter } from "utils/reportUtils"
import { ReactComponent as InfoIcon } from "assets/icons/info.svg"
import Badge, { BadgeColorEnum } from "components/UI/Badge"
import { TWithdrawStatus } from "types/withdrawal"
import { FORMAT_NUMBER_OPTIONS_USD } from "constants/format"
import { STATUS_COLORS } from "./constants"

import styles from "./withdrawal.module.scss"

type TParams = {
	amount: string
	currency: string
	rate: number
	status: TWithdrawStatus
	date: string
	address: string
	txid?: string
	note?: string
}

const WithdrawalDetails: React.FC<TDefaultModalProps & TParams> = ({
	amount,
	currency,
	rate,
	status,
	date,
	address,
	txid,
	note,
	updateModal,
}) => {
	const { formatNumber, formatMessage } = useIntl()
	const share = useShare()
	const copy = useCopyClick()

	useEffect(() => {
		const customTitle = (
			<>
				<button type="button" className={styles.modalInfoButton} id={styles.modalInfoButton}>
					<InfoIcon />
				</button>

				<div className={styles.modalHeaderCurrency}>
					<CurrencyIcon name={currency} size={20} />
					<span>{currency}</span>
				</div>
			</>
		)

		updateModal({ title: customTitle })
	}, [currency, updateModal])

	const renderConverted = (value: string | number) =>
		`≈ $${formatNumber(+value * (rate || 0), FORMAT_NUMBER_OPTIONS_USD)}`

	return (
		<>
			<div className={styles.modalAmountTitle}>
				<strong>{formatMessage(commonMessages.status_sent)}</strong>
				<span>
					{formatNumber(+amount, {
						minimumFractionDigits: 2,
						maximumFractionDigits: 8,
					})}
				</span>
				{renderConverted(amount)}
			</div>

			<div className={styles.modalDetailsTable}>
				<div>
					<div>{formatMessage(historyMessages.orders_table_status)}</div>
					<div>
						<Badge color={STATUS_COLORS[status.id] || BadgeColorEnum.GREY_DARK} alpha>
							{splitWords(capitalizeFirstLetter(status.label))}
						</Badge>
					</div>
				</div>
				<div>
					<div>{formatMessage(historyMessages.date_and_time)}</div>
					<div>{formatDate(date)}</div>
				</div>
				<div>
					<div>{formatMessage(historyMessages.address)}</div>
					<div>
						{address ? (
							<>
								<Ellipser value={address} style={{ width: 90 }} />
								<Button
									key={`share-${address}`}
									icon="ai-share_outline"
									size="mini"
									kind="outlined"
									loading="auto"
									onClick={e => {
										e.stopPropagation()
										share?.({ text: address })
									}}
								/>
								<Button
									key={`copy-${address}`}
									icon="ai-copy_new"
									size="mini"
									kind="outlined"
									loading="auto"
									onClick={e => {
										e.stopPropagation()
										copy(address)
									}}
								/>
							</>
						) : (
							" - "
						)}
					</div>
				</div>
				<div>
					<div>{formatMessage(historyMessages.txid)}</div>
					<div>
						{txid ? (<>
							<Ellipser value={txid} style={{ width: 90 }} />
							<Button
								key={`share-${txid}`}
								icon="ai-share_outline"
								size="mini"
								kind="outlined"
								loading="auto"
								onClick={e => {
									e.stopPropagation()
									share?.({ text: txid })
								}}
							/>
							<Button
								key={`copy-${txid}`}
								icon="ai-copy_new"
								size="mini"
								kind="outlined"
								loading="auto"
								onClick={e => {
									e.stopPropagation()
									copy(txid)
								}}
							/>
						</>) : "–"}
					</div>
				</div>
				<div>
					<div>{formatMessage(financeMessages.your_note)}</div>
					<div>{note && note.length > 0 ? note : "–"}</div>
				</div>
			</div>
		</>
	)
}

export default WithdrawalDetails
